import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Radio,
  Spinner,
  TextInput,
  Toast,
  ToastToggle,
} from "flowbite-react";
import { HiHome, HiPlus, HiCheck, HiX, HiTrash } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import {
  getAllPricePairs,
  createPricePair,
  activatePricePair,
  deletePricePair,
} from "../../services/paymentsPricePairs";
import type {
  PricePair,
  CreatePricePairRequest,
} from "../../types/paymentsPricePairs";
import { blueThemeSpinner } from "../exercises/ExerciseModal";

const ABTestingPage: FC = function () {
  const [pricePairs, setPricePairs] = useState<PricePair[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<"success" | "error" | null>(null);
  const [formData, setFormData] = useState<CreatePricePairRequest>({
    daily_price: 0,
    monthly_price: 0,
    yearly_price: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [savingPairId, setSavingPairId] = useState<string | null>(null);
  const [deletingPairId, setDeletingPairId] = useState<string | null>(null);

  const showToast = (message: string, type: "success" | "error") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastType(null);
      setToastMessage("");
    }, 5000);
  };

  const fetchPricePairs = async () => {
    try {
      setLoading(true);
      const response = await getAllPricePairs();
      setPricePairs(response.data.pricePairs);
    } catch (error) {
      console.error("Failed to fetch price pairs:", error);
      showToast("Failed to load price pairs", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricePairs();
  }, []);

  const handleCreatePricePair = async () => {
    if (
      formData.daily_price <= 0 ||
      formData.monthly_price <= 0 ||
      formData.yearly_price <= 0
    ) {
      showToast("Please enter valid prices", "error");
      return;
    }

    try {
      setSubmitting(true);
      await createPricePair(formData);
      showToast("Price pair created successfully", "success");
      setShowModal(false);
      setFormData({ daily_price: 0, monthly_price: 0, yearly_price: 0 });
      await fetchPricePairs();
    } catch (error) {
      console.error("Failed to create price pair:", error);
      showToast("Failed to create price pair", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivatePricePair = async (id: string) => {
    // Optimistic update - immediately update the UI
    const originalPricePairs = [...pricePairs];
    setPricePairs((prev) =>
      prev.map((pair) => ({
        ...pair,
        is_active: pair.id === id,
      })),
    );
    setSavingPairId(id);

    try {
      await activatePricePair(id);
      showToast("Price pair activated successfully", "success");
    } catch (error) {
      console.error("Failed to activate price pair:", error);
      showToast("Failed to activate price pair", "error");
      // Revert optimistic update on error
      setPricePairs(originalPricePairs);
    } finally {
      setSavingPairId(null);
    }
  };

  const handleItemClick = (id: string, event: React.MouseEvent) => {
    // Prevent activation when clicking on the delete button
    if ((event.target as HTMLElement).closest("button")) {
      return;
    }
    handleActivatePricePair(id);
  };

  const handleDeletePricePair = async (id: string) => {
    setDeletingPairId(id);

    try {
      await deletePricePair(id);
      showToast("Price pair deleted successfully", "success");
      await fetchPricePairs();
    } catch (error) {
      console.error("Failed to delete price pair:", error);
      showToast("Failed to delete price pair", "error");
    } finally {
      setDeletingPairId(null);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const formatMonthlyEquivalent = (yearlyPrice: number) => {
    const monthlyEquivalent = yearlyPrice / 12;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(monthlyEquivalent);
  };

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="p-4">
        <div className="max-w-6xl">
          <div className="mb-3">
            <Breadcrumb className="mb-3">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem>Membership</BreadcrumbItem>
              <BreadcrumbItem href="/membership/ab-testing">
                A/B Testing
              </BreadcrumbItem>
            </Breadcrumb>
          </div>
          <div className="mb-4 relative">
            <div className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl mb-4">
              A/B Testing - Price Pairs
            </div>
            {!loading && (
              <div className="absolute top-0 left-0 w-full max-w-2xl flex justify-end">
                <Button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2"
                >
                  <HiPlus className="h-4 w-4" />
                  Add Price Pair
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center p-6">
              <Spinner theme={blueThemeSpinner} color="info" size="lg" />
              <span className="ml-3 text-gray-500 dark:text-gray-400">
                Loading price pairs...
              </span>
            </div>
          ) : (
            <Card className="p-4 max-w-2xl">
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      Price Pairs
                    </h3>
                    {savingPairId && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <span>Saving...</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    Manage pricing configurations for A/B testing. Only one
                    price pair can be active at a time.
                  </p>
                </div>

                {pricePairs.length === 0 ? (
                  <div className="text-center p-6">
                    <p className="text-gray-500 dark:text-gray-400">
                      No price pairs found. Create one to get started.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pricePairs.map((pricePair) => (
                      <div
                        key={pricePair.id}
                        onClick={(e) => handleItemClick(pricePair.id, e)}
                        className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                          pricePair.is_active
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400"
                            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Radio
                              id={`active-${pricePair.id}`}
                              name="activePricePair"
                              checked={pricePair.is_active}
                              readOnly
                              className="text-blue-600 focus:ring-blue-500 pointer-events-none"
                            />
                            <div className="flex gap-6">
                              <div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  Daily Price
                                </div>
                                <div className="text-xl font-bold text-gray-900 dark:text-white">
                                  {pricePair.daily_price !== null
                                    ? formatPrice(pricePair.daily_price)
                                    : "N/A"}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  Monthly Price
                                </div>
                                <div className="text-xl font-bold text-gray-900 dark:text-white">
                                  {formatPrice(pricePair.monthly_price)}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  Yearly Price
                                </div>
                                <div className="text-xl font-bold text-gray-900 dark:text-white">
                                  {formatPrice(pricePair.yearly_price)}{" "}
                                  <span className="text-sm text-gray-400 dark:text-gray-500 font-normal">
                                    (
                                    {formatMonthlyEquivalent(
                                      pricePair.yearly_price,
                                    )}
                                    /mo)
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePricePair(pricePair.id);
                            }}
                            disabled={
                              pricePair.is_active ||
                              deletingPairId === pricePair.id
                            }
                            className="flex items-center gap-1"
                          >
                            {deletingPairId === pricePair.id ? (
                              <div className="flex items-center justify-center w-3 h-3">
                                <Spinner size="sm" />
                              </div>
                            ) : (
                              <HiTrash className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Create Price Pair Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <ModalHeader>Create New Price Pair</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <Label htmlFor="dailyPrice" className="mb-2 block">
                Daily Price ($)
              </Label>
              <TextInput
                id="dailyPrice"
                type="number"
                step="0.01"
                value={formData.daily_price || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    daily_price: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="Enter daily price (e.g., 1.99)"
                required
              />
            </div>
            <div>
              <Label htmlFor="monthlyPrice" className="mb-2 block">
                Monthly Price ($)
              </Label>
              <TextInput
                id="monthlyPrice"
                type="number"
                step="0.01"
                value={formData.monthly_price || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    monthly_price: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="Enter monthly price (e.g., 9.99)"
                required
              />
            </div>
            <div>
              <Label htmlFor="yearlyPrice" className="mb-2 block">
                Yearly Price ($)
              </Label>
              <TextInput
                id="yearlyPrice"
                type="number"
                step="0.01"
                value={formData.yearly_price || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    yearly_price: parseFloat(e.target.value) || 0,
                  })
                }
                placeholder="Enter yearly price (e.g., 99.99)"
                required
              />
            </div>
            {formData.daily_price > 0 &&
              formData.monthly_price > 0 &&
              formData.yearly_price > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Preview:</strong> Daily:{" "}
                    {formatPrice(formData.daily_price)}, Monthly:{" "}
                    {formatPrice(formData.monthly_price)}, Yearly: {""}
                    {formatPrice(formData.yearly_price)} (
                    {formatMonthlyEquivalent(formData.yearly_price)}/mo)
                  </p>
                </div>
              )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleCreatePricePair} disabled={submitting}>
            {submitting ? "Creating..." : "Create Price Pair"}
          </Button>
          <Button color="light" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Toast Notification */}
      {toastType && (
        <div className="fixed bottom-4 right-4 z-50">
          <Toast>
            <div
              className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                toastType === "success"
                  ? "bg-green-100 text-green-500 dark:bg-green-800 dark:text-green-200"
                  : "bg-red-100 text-red-500 dark:bg-red-800 dark:text-red-200"
              }`}
            >
              {toastType === "success" ? (
                <HiCheck className="h-5 w-5" />
              ) : (
                <HiX className="h-5 w-5" />
              )}
            </div>
            <div className="ml-3 text-sm font-normal">{toastMessage}</div>
            <ToastToggle onDismiss={() => setToastType(null)} />
          </Toast>
        </div>
      )}
    </NavbarSidebarLayout>
  );
};

export default ABTestingPage;
